import clsx from 'clsx';
import {
  forwardRef,
  useContext,
  useId,
  useImperativeHandle,
  useRef,
  type ChangeEvent,
} from 'react';

import {
  type DataTestId,
  type StylingProps,
  type WithChildren,
  type AriaLabelingProps,
  type DOMProps,
} from '@dynatrace/strato-components/core';
import { _mergeProps } from '@dynatrace/strato-components/core';
import { Text } from '@dynatrace/strato-components/typography';

import { RadioGroupContext } from './RadioGroupContext.js';
import * as styles from './styles.sty.js';
import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import type { FocusEvents } from '../../core/types/events.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { fieldColorsCSS } from '../../styles/field.sty.js';
import { useFormFieldContext } from '../forms-core/hooks/useFormFieldContext.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useFormFieldValidation } from '../forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import type { FormControlRef } from '../shared-types.js';

// #region Typings
/**
 * Properties for the Radio component
 * @public
 */
export type RadioProps = Pick<
  FormControlProps<string, never>,
  'disabled' | 'value'
> &
  DOMProps &
  StylingProps &
  WithChildren &
  DataTestId &
  FocusEvents &
  AriaLabelingProps;
//#endregion

/**
 * Radio buttons allow users to select one option from
 * a group of related options. To group options, wrap the
 * `Radio` components inside a `RadioGroup` component.
 * If you want to give the user more than 4 options,
 * consider using a `Select` component.
 * @public
 */
export const Radio = /* @__PURE__ */ forwardRef<FormControlRef, RadioProps>(
  (props, forwardedRef) => {
    const {
      disabled: propDisabled,
      id: propId,
      children,
      value,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    }: RadioProps = props;

    const { dispatch: messagesContextDispatch } = useFormFieldMessagesContext();

    const radioGroupContext = useContext(RadioGroupContext);

    if (isDev() && !radioGroupContext) {
      // eslint-disable-next-line no-console
      console.warn(
        '<Radio /> can only be used as a child element of <RadioGroup />',
      );
    }

    const {
      isDisabled,
      ariaDisabled,
      controlState,
      hasError,
      readOnly,
      name: groupName,
      required: groupRequired,
      value: groupValue,
      onChange,
    } = radioGroupContext || {};

    // Imperative Handle Construction
    const radioLabelRef = useRef<HTMLLabelElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(forwardedRef, (): FormControlRef<HTMLLabelElement> => {
      return {
        element: radioLabelRef.current,
        inputRef: inputRef.current,
        focus() {
          inputRef.current?.focus();
        },
        validate() {
          if (controlState !== undefined) {
            return;
          }

          inputRef.current?.checkValidity();
          messagesContextDispatch?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
        },
      };
    });

    const {
      onChange: validationOnChange,
      onBlur: validationOnBlur,
      onInvalid: validationOnInvalid,
      ariaProps,
    } = useFormFieldValidation(inputRef);

    const fallbackId = useId();
    const fieldContext = useFormFieldContext();

    const disabled = propDisabled ?? isDisabled ?? fieldContext.disabled;
    const disabledOrAriaDisabled = disabled || ariaDisabled || false;

    const checked = value === groupValue;

    function handleChange(e: ChangeEvent<HTMLInputElement>) {
      if (readOnly) {
        return;
      }

      onChange?.(e.target.value);
      !controlState && validationOnChange?.();
    }

    let variant: 'accent' | 'emphasized' | 'default';
    let color: 'critical' | 'primary' | 'neutral' | 'success' | 'warning' =
      'neutral';

    if (checked) {
      variant = 'accent';
    } else if (hasError) {
      variant = 'emphasized';
    } else {
      variant = 'default';
    }

    if (hasError) {
      color = 'critical';
    } else if (checked) {
      color = 'primary';
    } else {
      color = 'neutral';
    }

    const { focusProps, focusClassName, isFocusVisible } = useFocusRing({
      color,
      disabled,
    });

    return (
      <label
        data-testid={dataTestId}
        htmlFor={propId || fallbackId}
        ref={radioLabelRef}
        className={clsx(
          styles.label,
          {
            [styles.disabled]: disabledOrAriaDisabled,
            [styles.checked]: checked,
          },
          consumerClassName,
        )}
        style={consumerStyle}
      >
        <VisuallyHidden>
          <input
            ref={inputRef}
            checked={checked}
            aria-checked={checked}
            name={groupName}
            value={value}
            type="radio"
            disabled={disabled}
            aria-disabled={ariaDisabled}
            required={groupRequired}
            id={propId || fallbackId}
            onChange={handleChange}
            onInvalid={(e) => {
              if (controlState) {
                e.preventDefault(); // Prevents the browser validation pop-up
                return;
              }

              validationOnInvalid(e);
            }}
            readOnly={readOnly}
            {..._mergeProps(
              remainingProps,
              focusProps,
              controlState
                ? {}
                : {
                    ...ariaProps,
                    onBlur: validationOnBlur,
                  },
            )}
          />
        </VisuallyHidden>
        <div
          className={clsx(
            styles.knob({
              disabled: disabledOrAriaDisabled,
              checked,
              isFocusVisible,
              hasError,
              readOnly,
            }),
            fieldColorsCSS({
              disabled: disabledOrAriaDisabled,
              variant,
              color,
            }),
            focusClassName,
          )}
        >
          <svg
            className={clsx(
              styles.innerKnob,
              fieldColorsCSS({
                color: hasError ? 'critical' : 'neutral',
                disabled: disabledOrAriaDisabled,
                variant: 'accent',
              }),
            )}
            viewBox="0 0 100 100"
          >
            <circle r={isFocusVisible ? '20' : '25'} cx="50" cy="50" />
          </svg>
        </div>
        <Text as="span" textStyle="base">
          {children}
        </Text>
      </label>
    );
  },
);

(Radio as typeof Radio & { displayName: string }).displayName = 'Radio';
